import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface DemographicsBreakdownItem {
  value: string;
  count: number;
  percentage: number;
  users: { id: string; firstName: string; lastName: string | null }[];
}

export interface QuizFieldOption {
  id: string;
  label: string;
  type: 'single' | 'multiple' | 'scale';
}

export interface DemographicsFilter {
  field: string;
  values: string[];
}

export interface DemographicsData {
  totalUsers: number;
  usersWithQuiz: number;
  groupByField: string;
  breakdown: DemographicsBreakdownItem[];
  cityBreakdown: Record<string, Record<string, number>>;
  quizFields: QuizFieldOption[];
  cities: { id: string; name: string }[];
  appliedFilters: {
    city: string;
    filters: DemographicsFilter[];
  };
}

export interface UseAdminDemographicsParams {
  groupBy: string;
  city?: string;
  filters?: DemographicsFilter[];
}

export interface DemographicsResponse {
  success: boolean;
  data?: DemographicsData;
  error?: string;
}

// Serialize filters to URL format: field1:val1,val2|field2:val3,val4
export function serializeFilters(filters: DemographicsFilter[]): string {
  return filters
    .filter(f => f.field && f.values.length > 0)
    .map(f => `${f.field}:${f.values.join(',')}`)
    .join('|');
}

// Parse filters from URL format
export function parseFilters(filtersStr: string): DemographicsFilter[] {
  if (!filtersStr) return [];
  return filtersStr.split('|').map(part => {
    const [field, valuesStr] = part.split(':');
    return {
      field,
      values: valuesStr ? valuesStr.split(',').filter(Boolean) : [],
    };
  }).filter(f => f.field && f.values.length > 0);
}

/**
 * Hook to fetch demographics/user insights data (admin only)
 */
export function useAdminDemographics(params: UseAdminDemographicsParams) {
  const filtersKey = params.filters ? serializeFilters(params.filters) : '';
  
  return useQuery({
    queryKey: ['admin-demographics', params.groupBy, params.city, filtersKey],
    queryFn: async (): Promise<DemographicsResponse> => {
      const searchParams = new URLSearchParams();
      searchParams.set('groupBy', params.groupBy);
      if (params.city && params.city !== 'all') {
        searchParams.set('city', params.city);
      }
      if (params.filters && params.filters.length > 0) {
        searchParams.set('filters', serializeFilters(params.filters));
      }
      return api.get(`/admin/demographics?${searchParams.toString()}`);
    },
  });
}
